<template>
  <Information v-if="isLoading || !isFullyAllocated">
    <template #trigger>
      <Spinner v-if="isLoading" class="size-4 text-obsidian-400" />
      <Link2OffIcon v-else class="size-4 shrink-0 text-bittersweet-600" />
    </template>
    <template #modal>
      <template v-if="isLoading">
        {{ t('loadingAllocations') }}
      </template>
      <template v-else>
        {{
          t('partiallyAllocatedTooltip', {
            allocated: formatCurrency(allocatedAmount),
            total: formatCurrency(totalAmount),
            type: t(`type.${type}`),
          })
        }}
      </template>
    </template>
  </Information>
</template>

<script lang="ts" setup>
import Information from '@/components/Information.vue';
import Spinner from '@/components/icons/Spinner.vue';
import { formatCurrency } from '@/utils/format-numbers';
import { isClose } from '@/utils/is-zero';
import { Link2OffIcon } from 'lucide-vue-next';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  isLoading: boolean;
  allocatedAmount: number;
  totalAmount: number;
  type: 'invoice' | 'payment';
}>();

const { t } = useI18n();

const isFullyAllocated = computed(() => isClose(props.allocatedAmount, props.totalAmount));
</script>

<i18n lang="json">
{
  "en": {
    "loadingAllocations": "Loading allocations…",
    "partiallyAllocatedTooltip": "{allocated} of {total} of the {type} are allocated to ledger entries — allocation incomplete",
    "type": {
      "invoice": "invoice",
      "payment": "payment"
    }
  },
  "de": {
    "loadingAllocations": "Zuordnungen werden geladen…",
    "partiallyAllocatedTooltip": "{allocated} von insgesamt {total} der {type} sind Forderungen zugeordnet — Zuordnung unvollständig",
    "type": {
      "invoice": "Rechnung",
      "payment": "Zahlung"
    }
  },
  "es": {
    "loadingAllocations": "Cargando imputaciones…",
    "partiallyAllocatedTooltip": "{allocated} de {total} en concepto de {type} están imputados a partidas del libro — imputación incompleta",
    "type": {
      "invoice": "factura",
      "payment": "pago"
    }
  }
}
</i18n>
